import { User } from "./user";
import { Talk } from "./talk";

import { Util } from "../providers/util/util";
import { Appointment } from "./appointment";

// ion-segment-button valueと合わせる
export enum ChatRoomsTab {
  Matched = "matched",
  Contacted = "contacted",
}

export enum LikeTab {
  Like = "like",
  Liked = "liked",
  RichLiked = "rich_liked",
  RoyalLiked = "royal_option_liked",
}

export const LikeFrom = {
  SearchPage: "search",
  UserProfilePageFromSearchPage: "user-profile-from-search",
  UserProfilePage: "user-profile",
  DailySwipe: "daily-swipe",
  Activity: "activity",
  Meeting: "meeting",
  SettingHide: "setting-hide",
  Tutorial: "tutorial",
  SettingReport: "setting-report",
  RoyalPickupPage: "royal-pickup",
  UserProfileFromRoyalPickupPage: "user-profile-royal-pickup",
};

export const PLACEHOLDER =
  "右上の「編集」ボタンから編集できます。\r\n\r\n〇〇さん、△△月 XX 日に初めて会った\r\nイタリアンが好き\r\n今月中にもう1回ご飯行く";

export class ChatRoom {
  id: number;
  users: User[];
  last_talked_at: Date;
  last_talked_user_id: number;
  unread_num: string;
  last_message: Talk;
  favorite_rank: number;
  custom_display_room_name: string;
  tempMessage: string;
  first_talk_stamp_id: number;
  appointment?: Appointment;
  matched_at?: Date;
  contacted_at?: Date;
  note: string;
  attached_name: string;
  met: boolean;

  constructor(fields: any) {
    for (const f in fields) {
      this[f] = fields[f];
    }
  }

  getRoomName(): string {
    return this.custom_display_room_name;
  }

  getUsers(): User[] {
    return this.users.map((user) => {
      return new User(user);
    });
  }

  getUserIds(): number[] {
    const users = this.getUsers();
    return users.map((user) => user.id);
  }

  getLastMessage(
    meId: number,
    isMeGyosha: boolean,
    fromTinderSwipe: boolean
  ): string {
    if (!this.last_message) {
      return "メッセージを交換していません";
    }

    if (
      (this.users.length === 1 && this.getUsers()[0].isDeleted()) ||
      (!isMeGyosha && this.getUsers()[0].isGyosha())
    ) {
      return "このユーザーは退会しました";
    }

    const lastMessage = new Talk(this.last_message);
    if (lastMessage.content === "question") {
      return "質問に回答しました。";
    }
    if (lastMessage.hasFreeText()) {
      return fromTinderSwipe
        ? lastMessage.getFreeText().replace(/<br>/g, "")
        : lastMessage.getFreeText();
    }
    if (lastMessage.hasImage()) return "画像を送信しました";
    if (lastMessage.hasLikeStamp()) {
      if (lastMessage.getUserId() === meId) {
        return "いいねを送信しました";
      } else {
        return "いいねを受信しました";
      }
    }
    if (lastMessage.hasRichLikeStamp()) {
      if (lastMessage.getUserId() === meId) {
        return "リッチいいねを送信しました";
      } else {
        return "リッチいいねを受信しました";
      }
    }
    if (lastMessage.hasChocolateStamp()) {
      if (lastMessage.getUserId() === meId) {
        return "チョコを送信しました";
      } else {
        return "チョコを受信しました";
      }
    }
    if (lastMessage.hasWhiteChocolateStamp()) {
      if (lastMessage.getUserId() === meId) {
        return "お菓子を送信しました";
      } else {
        return "お菓子を受信しました";
      }
    }
    if (lastMessage.hasOnedariChocolateStamp()) {
      if (lastMessage.getUserId() === meId) {
        return "おねだりスタンプを送信しました";
      } else {
        return "おねだりスタンプを受信しました";
      }
    }
  }

  getLastTalkUserId(): number {
    return this.last_talked_user_id;
  }

  getUnreadNum(): string {
    return this.unread_num;
  }

  hasUnreadTalk(): boolean {
    return Number(this.unread_num) > 0;
  }

  getTalkedUser(userId: number): User {
    return this.getUsers().find((user) => user.id === userId);
  }

  getLastTalkedAtStr(): string {
    if (!(this.last_talked_at instanceof Date)) {
      this.last_talked_at = new Date(this.last_talked_at);
    }
    // 86400000 = 60sec * 60min * 24h
    const termDay =
      Math.ceil(
        (new Date().getTime() - this.last_talked_at.getTime()) / 86400000
      ) - 1;

    if (termDay >= 1) {
      return termDay.toString() + "日前";
    }
    return Util.formatPostedTime(this.last_talked_at);
  }

  getLikeType(): string {
    switch (this.first_talk_stamp_id) {
      case 5:
        return "rich";
      default:
        return "basic";
    }
  }

  setUnreadTalkNum(num: string): void {
    this.unread_num = num;
  }

  readAll(): void {
    this.setUnreadTalkNum("0");
  }

  favoriteRank(): number {
    return this.favorite_rank;
  }

  setFavoriteRank(num: number): void {
    this.favorite_rank = num;
  }

  getNote(): string {
    return this.note;
  }

  setNote(note: string): void {
    this.note = note;
  }

  getDisplayName(nickname: string): string {
    return !this.attached_name ? nickname : this.attached_name;
  }

  setAttachedName(attachedName: string): void {
    this.attached_name = attachedName;
  }

  isLastMessageOtherSendStamp(meId: number): boolean {
    if (this.getUsers() && this.getUsers()[0] && this.getUsers()[0].isDeleted())
      return false;
    const lastMessage: Talk = new Talk(this.last_message);
    if (lastMessage.hasLikeStamp()) {
      return lastMessage.getUserId() !== meId;
    }
    return false;
  }

  isMatched(): boolean {
    return !!this.matched_at;
  }

  isContacted(): boolean {
    return !!this.matched_at && !!this.contacted_at;
  }

  getAppointment(): Appointment {
    return new Appointment(this.appointment);
  }

  getAppointmentState(): string {
    const state = this.getAppointment().state;
    if (!state) return "";

    return state;
  }

  getAppointmentStateLabel(): string {
    if (this.getAppointment()) {
      switch (this.getAppointmentState()) {
        case "pending":
          return "お約束回答待ち";
        case "agreed":
          return "お約束済み";
        default:
          return "";
      }
    } else {
      return "";
    }
  }

  setAppointment(appointment: Appointment): void {
    this.appointment = appointment;
  }
}



// WEBPACK FOOTER //
// ./src/models/chat-room.ts